import { Attr, Field, Model } from '@mode-switch/model';
import { QueryResult, RecordsPlan, RecordsResult, SelectPlan, SelectResult } from '@mode-switch/query';
import { DataType as VegasDataType, Encoding, VariableType } from '@mode-vegas/vegas';

import { Switch } from '@mode/shared/util-switch';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { DataType } from '@mode-switch/express';

type ModeRole = FlamingoTypes.ModeRole;

/* eslint-disable @typescript-eslint/no-namespace */
export namespace Flamingo {
  export function getRecordsInline(request: SelectPlan<ModeRole>, result: SelectResult<ModeRole>) {
    return RecordsResult.recordsInline(request as RecordsPlan<ModeRole>, result);
  }

  export function getCountValue(request: SelectPlan<ModeRole>, result: SelectResult<ModeRole>) {
    return RecordsResult.countValue(request as RecordsPlan<ModeRole>, result);
  }

  export function tableColumns(
    model: FlamingoTypes.ModeModel,
    viewName: string,
    encoding: Encoding
  ): Array<QueryResult.Column<ModeRole>> {
    const fromFields = tableEncodingFields(model, viewName, encoding);
    return fromFields.map((fromField) => {
      const {
        alias,
        formula: { source, type, role },
      } = fromField;
      return QueryResult.Column.make<ModeRole>(fromField.name, alias, source, type, role);
    });
  }

  export function tableEncodingFields(model: FlamingoTypes.ModeModel, viewName: string, encoding: Encoding) {
    const view = Model.Views.find(model, viewName);
    if (view == null) {
      throw new Error(`Cannot find view with name ${viewName} in model ${model.name}`);
    }

    const fields = new Array<Field<ModeRole>>();
    for (const field of encoding.marks.properties.text.values) {
      // TODO: Convert to use CompiledField abstraction
      const compiled = Switch.field(model, viewName, field.formula, Attr.Type.Discrete, field.id);
      if (compiled != null) {
        fields.push(compiled);
      }
    }

    return fields;
  }

  // Minimum bag of props we need to validate a filter
  export type ReportFilterLike = {
    formula: string;
    formulaType: VegasDataType;
    dataType: VegasDataType;
    varType: VariableType;
  };

  export function modelHasSomeMatchingField<T extends ReportFilterLike>(
    model: FlamingoTypes.ModeModel,
    filter: T
  ): boolean {
    return model.views.some((view) => {
      return getValidReportFilters(model, view.name, [filter]).length !== 0;
    });
  }

  /**
   * @param model Model that includes the view found at `viewName`
   * @param viewName aka. `queryToken`. Name of the view to compile the filter with
   * @param filters Report filters to validate against the requested view
   * @returns An array of filters that 1) have a formula that compiles successfully
   * with the requested view, and 2) have a datatype that matches their compiled formula's
   * data type
   */
  export function getValidReportFilters<T extends ReportFilterLike>(
    model: FlamingoTypes.ModeModel,
    viewName: string,
    filters: readonly T[]
  ): T[] {
    // Remove formulas that:
    // ---
    // 1. Don't exist in the view's schema
    // 2. Don't have matching data types
    return filters.filter((control) => {
      const compiled = Switch.compile(model, viewName, control.formula);
      const expr = Switch.getCompiledExpr(compiled);

      // Case #1
      // eslint-disable-next-line no-eq-null, eqeqeq
      if (expr == null) {
        return false;
      }

      // Case #2
      const cType = control.formulaType || control.dataType;
      switch (expr.data.type.name) {
        case DataType.Name.Null: {
          return cType === VegasDataType.NULL;
        }
        case DataType.Name.String: {
          return cType === VegasDataType.STRING;
        }
        case DataType.Name.Boolean: {
          return cType === VegasDataType.BOOLEAN;
        }
        case DataType.Name.DateTime:
        case DataType.Name.Timestamp: {
          switch (cType) {
            case VegasDataType.DATE:
            case VegasDataType.DATETIME: {
              return true;
            }
          }
          break;
        }
        case DataType.Name.Float:
        case DataType.Name.Decimal:
        case DataType.Name.Integer: {
          switch (cType) {
            case VegasDataType.FLOAT:
            case VegasDataType.INTEGER:
            case VegasDataType.NUMBER: {
              return true;
            }
            case VegasDataType.DATEMONTH:
            case VegasDataType.DATEQTR:
            case VegasDataType.DATEWKDAY: {
              return Switch.Expressions.isDatePart(expr);
            }
          }
          break;
        }
        case DataType.Name.Regexp: {
          switch (cType) {
            case VegasDataType.STRING:
            case VegasDataType.REGEXP: {
              return true;
            }
          }
          break;
        }
      }

      return false;
    });
  }
}
